/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { ButtonList, CosmosRichText, CosmosText, CosmosTextVariantEnum } from "@components/elements";
import { css } from "@panda/css";
import { token } from "@panda/tokens";
import { useMemo } from "react";

import { ctaCardStyles } from "./AccordionTierCard.styles";

import type { AccordionTierCtaCardFragment, CosmosEnumEdsGradients } from "@cosmos/hygraph-schema";

/**
 * The author wraps their highlight text in <a href="#">…</a> inside the rich-text editor —
 * matches the cosmos-wide gradient-link convention (Hygraph's RichText AST round-trips
 * `<a>` cleanly; `<span class>` is stored as literal text by the AST parser and renders
 * as visible markup).
 *
 * We render the html directly via `dangerouslySetInnerHTML` instead of going through
 * `CosmosRichText` because CosmosRichText sets its own `& a[href]` rule
 * (`color: eds.fill.primary.default`, `text-decoration: underline`) that beats our
 * `& a` gradient rule on specificity AND overrides the `color: transparent` we need
 * for `background-clip: text` to reveal the gradient. Direct render = no competing
 * anchor styles. `& p` margins are zeroed so the wrapper paragraph (Hygraph's
 * RichTextAST always produces a `<p>` root) doesn't add headline-breaking spacing.
 *
 * `pointer-events: none` keeps the anchor decorative — these aren't real nav targets.
 */
const titleGradientStyles = css({
	textStyle: "headingSM",
	color: "eds.text.primary",
	"& p": { margin: 0 },
	// `&& a` (doubled specificity) — matches the ColorfulTitle pattern. Needed because
	// CosmosRichText's `& a[href]` rule (color: primary, text-decoration: underline)
	// would otherwise win the cascade. Panda auto-prefixes `background-clip: text`.
	"&& a": {
		background: "var(--cta-highlight-bg, none)",
		backgroundClip: "text",
		color: "transparent",
	},
	'& a[href="#"]': {
		pointerEvents: "none",
		textDecoration: "none",
		cursor: "default",
	},
});

export interface TierCtaCardProps extends AccordionTierCtaCardFragment {
	/** Fallback gradient when the CTA-level `gradient` is not set — inherited from the tier. */
	fallbackGradient?: CosmosEnumEdsGradients | null;
}

/**
 * Bottom-of-tier CTA strip. Image asset on the left (uploaded via Hygraph),
 * gradient-highlighted headline + supporting copy in the middle, one or more CTA
 * buttons on the right. The asset replaces the layered icon-stack treatment from
 * the original Figma so designers/authors can swap in any composed illustration.
 *
 * `buttons` is the modular Button | IconButton union; rendered through `ButtonList`
 * which spreads each entry into `CosmosButton` (icon-buttons surface their `icon` /
 * `iconPosition` automatically).
 */
export const TierCtaCard = ({
	title,
	desc,
	buttons,
	asset,
	gradient,
	fallbackGradient,
}: TierCtaCardProps): React.JSX.Element | null => {
	// Memoized because React 19 compares the dangerouslySetInnerHTML wrapper by
	// reference, so an inline literal re-parses the title on any re-render while
	// the tier is open — an ancestor render, or Radix's own height measurement.
	// (Not on collapse/expand: Content unmounts without `forceMount`, so that
	// cycle rebuilds the DOM either way.) Same defect and fix as CosmosRichText,
	// MW-13210 — see the rationale there.
	//
	// Above the early return below: hooks must run unconditionally.
	const titleHtml = useMemo(() => (title?.html ? { __html: title.html } : null), [title?.html]);

	if (!title?.html && !title?.text && !desc && !buttons?.length) return null;

	const resolvedGradient = gradient || fallbackGradient || null;
	const highlightVar = resolvedGradient
		? ({ ["--cta-highlight-bg"]: token(`gradients.eds.gradient.${resolvedGradient}`) } as React.CSSProperties)
		: undefined;

	return (
		<div className={ctaCardStyles}>
			{asset?.url ? (
				<div className="cta-asset-stack">
					<img className="cta-asset-glow" src={asset.url} alt="" aria-hidden="true" />
					<img className="cta-asset" src={asset.url} alt={asset.altText || ""} />
				</div>
			) : null}

			<div className="cta-text">
				{titleHtml ? (
					<h3 className={`cta-title ${titleGradientStyles}`} style={highlightVar} dangerouslySetInnerHTML={titleHtml} />
				) : title?.text ? (
					<CosmosText className="cta-title" variant={CosmosTextVariantEnum.headingSM} as="h3">
						{title.text}
					</CosmosText>
				) : null}
				{desc ? (
					<CosmosRichText className="cta-desc" variant={CosmosTextVariantEnum.paragraphSM} as="div" html={desc} />
				) : null}
			</div>

			{buttons?.length ? (
				<div className="cta-button">
					<ButtonList buttons={buttons} size="md" />
				</div>
			) : null}
		</div>
	);
};

TierCtaCard.displayName = "AccordionTierCard.CtaCard";
